import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../DarkModeContext";
import Header from "../components/Header";
import TabBar from "../components/TabBar";
import { useAuth } from "../AuthContext";

export default function TermsOfUse() {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { user } = useAuth();

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${
      darkMode ? "text-gray-100" : "text-gray-800"
    }`}>
      {/* Background */}
      <div className={`fixed inset-0 -z-10 ${
        darkMode
          ? "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-b from-white via-gray-50 to-[#f9f9f9]"
      }`} />

      <Header />

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-20 pb-32">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className={`mb-6 inline-flex items-center gap-2 text-sm font-medium transition-colors ${
            darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Title */}
        <h1 className={`text-3xl font-bold mb-6 ${
          darkMode ? "text-gray-100" : "text-gray-900"
        }`} style={{ fontFamily: 'Quicksand, sans-serif' }}>
          Terms of Use
        </h1>

        {/* Content */}
        <div className={`prose prose-sm max-w-none ${
          darkMode ? "prose-invert" : ""
        }`}>
          <p className={`text-sm mb-6 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            Last Updated: November 16, 2025
          </p>

          <p className="mb-6">
            Welcome to Tour in a Box ("Company," "we," "our," or "us"). These Terms of Use ("Terms") govern your use of our websites, web apps, physical products, digital tours, narratives, riddles, and any related services (collectively, the "Services").
          </p>

          <p className="mb-6">
            By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            1. About the Experience
          </h2>
          <p className="mb-6">
            Tour in a Box LLC provides self-guided walking tours, puzzles, directions, and stories for entertainment and educational purposes. We do not supervise your activity, accompany you on the route, or control conditions in the physical environment. You are solely responsible for your own safety and decisions while participating.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            2. Eligibility
          </h2>
          <p className="mb-6">
            You must be at least 13 years old to use the Services. If under 18, you must have permission from a parent or legal guardian.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            3. User Responsibilities
          </h2>
          <p className="mb-4">By using the Services, you agree to:</p>
          <ul className={`list-disc pl-6 mb-4 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Follow all local laws, traffic rules, and safety guidelines</li>
            <li>Stay aware of surroundings, including vehicles, pedestrians, and environmental hazards</li>
            <li>Use your judgment when selecting routes, crossings, and timing</li>
            <li>Avoid entering restricted, unsafe, or private areas</li>
            <li>Ensure your phone has battery, data, and navigation capability</li>
            <li>Exercise caution in unfamiliar areas or in adverse weather</li>
          </ul>
          <p className="mb-6">
            If any location appears unsafe, you must choose an alternative path or stop participating.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            4. Not Emergency or Safety Services
          </h2>
          <p className="mb-4">
            Tour in a Box does not provide emergency assistance, security guidance, or professional navigation services. Do not rely on the Services in situations involving personal safety, emergencies, or hazardous conditions.
          </p>
          <p className="mb-6">
            In emergencies, call local authorities.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            5. Assumption of Risk
          </h2>
          <p className="mb-4">Walking tours involve inherent risks, including but not limited to:</p>
          <ul className={`list-disc pl-6 mb-4 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Uneven terrain or sidewalks</li>
            <li>Traffic or street crossings</li>
            <li>Weather conditions</li>
            <li>Crowds or public activity</li>
            <li>Construction zones</li>
            <li>Interaction with unfamiliar environments</li>
          </ul>
          <p className="mb-6">
            By using the Services, you acknowledge these risks and accept full responsibility for your participation.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            6. Intellectual Property
          </h2>
          <p className="mb-6">
            All content—including riddles, narratives, images, branding, instructions, and digital or physical materials—is owned by Tour in a Box LLC or its licensors. You may not copy, distribute, resell, publish, or reproduce any content without permission.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            7. Payments & Access
          </h2>
          <p className="mb-6">
            Purchases are non-refundable except where required by law. Digital tour access lasts for the duration specified at purchase. We may update or modify content at any time.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            8. Prohibited Conduct
          </h2>
          <p className="mb-4">You may not:</p>
          <ul className={`list-disc pl-6 mb-6 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Use the Services for illegal purposes</li>
            <li>Attempt to reverse-engineer, scrape, or replicate the content</li>
            <li>Harass or interfere with other participants or the public</li>
            <li>Engage in behavior that puts yourself or others at risk</li>
          </ul>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            9. Disclaimer of Warranties
          </h2>
          <p className="mb-4">
            The Services are provided on an "as-is," "as-available" basis without warranties of any kind.
          </p>
          <p className="mb-4">We do not guarantee:</p>
          <ul className={`list-disc pl-6 mb-4 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Accuracy of directions</li>
            <li>Availability of routes or locations</li>
            <li>Safety of any location</li>
            <li>Historical or factual accuracy of narratives</li>
            <li>Continuous or error-free operation</li>
          </ul>
          <p className="mb-6">
            Use the Services at your own risk.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            10. Limitation of Liability
          </h2>
          <p className="mb-4">To the fullest extent permitted by law:</p>
          <ul className={`list-disc pl-6 mb-4 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Tour in a Box LLC and its owners, employees, and contractors shall not be liable for any injuries, losses, damages, or expenses arising from your use of the Services.</li>
            <li>This includes but is not limited to bodily injury, property damage, lost profits, lost data, or any indirect or consequential damages.</li>
          </ul>
          <p className="mb-6">
            Your sole remedy for dissatisfaction with the Services is to stop using them.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            11. Indemnification
          </h2>
          <p className="mb-4">
            You agree to indemnify and hold harmless Tour in a Box LLC from any claims, damages, or expenses arising from:
          </p>
          <ul className={`list-disc pl-6 mb-6 space-y-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            <li>Your use of the Services</li>
            <li>Your violation of these Terms</li>
            <li>Your conduct during participation</li>
          </ul>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            12. Modifications to Terms
          </h2>
          <p className="mb-6">
            We may update these Terms from time to time. Continued use of the Services constitutes acceptance of the updated Terms.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            13. Governing Law
          </h2>
          <p className="mb-6">
            These Terms are governed by the laws of the state where Tour in a Box LLC is organized, without regard to conflict-of-law principles.
          </p>

          <h2 className={`text-xl font-bold mt-8 mb-4 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
            14. Contact
          </h2>
          <p className="mb-6">
            For questions, email: <a href="mailto:hello@tourinabox.com" className="text-blue-500 hover:text-blue-600 underline">hello@tourinabox.com</a>
          </p>
        </div>
      </main>

      <TabBar isAuthed={!!user} />
    </div>
  );
}
